import React from 'react';
import { Activity, AlertCircle, CheckCircle, Search, Trash2, XCircle } from 'lucide-react';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { TabsContent } from '../../../components/ui/tabs';

export const ActivityTab = ({ activityLogs, activitySummary, formatLogDate, getEventTypeColor, handleCleanupLogs, loadActivityLogs, loadingLogs, logEventType, logFilter, setLogEventType, setLogFilter }) => (
            <TabsContent value="activity">
              <div className="space-y-6">
                {/* Summary Cards */}
                {activitySummary && (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Card>
                      <CardContent className="pt-4">
                        <p className="text-sm text-muted-foreground">Last 24 Hours</p>
                        <p className="text-2xl font-bold">{activitySummary.total_24h}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <p className="text-sm text-muted-foreground">Last 7 Days</p>
                        <p className="text-2xl font-bold">{activitySummary.total_7d}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <p className="text-sm text-muted-foreground">Last 30 Days</p>
                        <p className="text-2xl font-bold">{activitySummary.total_30d}</p>
                      </CardContent>
                    </Card>
                    <Card className="border-green-200 bg-green-50/50">
                      <CardContent className="pt-4">
                        <p className="text-sm text-muted-foreground">Logins (24h)</p>
                        <p className="text-2xl font-bold text-green-700">
                          {activitySummary.event_counts_24h?.login || 0}
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                )}
  
                {/* Filters */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Search className="w-4 h-4" />
                      Filter Logs
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-4 flex-wrap">
                      <div className="flex-1 min-w-[200px]">
                        <Label htmlFor="log-email">Search by Email</Label>
                        <Input
                          id="log-email"
                          placeholder="user@email.com"
                          value={logFilter}
                          onChange={(e) => setLogFilter(e.target.value)}
                        />
                      </div>
                      <div className="w-48">
                        <Label htmlFor="log-type">Event Type</Label>
                        <select
                          id="log-type"
                          className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                          value={logEventType}
                          onChange={(e) => setLogEventType(e.target.value)}
                        >
                          <option value="">All Events</option>
                          <option value="login">Login</option>
                          <option value="login_failed">Login Failed</option>
                          <option value="invitation_sent">Invitation Sent</option>
                          <option value="invitation_accepted">Invitation Accepted</option>
                          <option value="invitation_rejected">Invitation Rejected</option>
                          <option value="invitation_expired">Invitation Expired</option>
                          <option value="tactical_created">Tactical Created</option>
                          <option value="tactical_updated">Tactical Updated</option>
                          <option value="admin_action">Admin Action</option>
                          <option value="error">Error</option>
                        </select>
                      </div>
                      <div className="flex items-end gap-2">
                        <Button onClick={() => loadActivityLogs(logFilter, logEventType)}>
                          <Search className="w-4 h-4 mr-2" />
                          Search
                        </Button>
                        <Button variant="outline" onClick={handleCleanupLogs}>
                          <Trash2 className="w-4 h-4 mr-2" />
                          Cleanup Old
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
  
                {/* Recent Errors */}
                {activitySummary?.recent_errors?.length > 0 && (
                  <Card className="border-red-200">
                    <CardHeader>
                      <CardTitle className="text-base text-red-700 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        Recent Errors
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {activitySummary.recent_errors.map((err, i) => (
                          <div key={err.id || `${err.timestamp}-${i}`} className="text-sm p-2 bg-red-50 rounded">
                            <span className="text-muted-foreground">{formatLogDate(err.timestamp)}</span>
                            <span className="mx-2">|</span>
                            <span>{err.details || err.event_type}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
  
                {/* Logs Table */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Activity Log</CardTitle>
                    <CardDescription>Recent system events (showing last 100)</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {loadingLogs ? (
                      <div className="text-center py-8 text-muted-foreground">Loading logs...</div>
                    ) : activityLogs.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">No activity logs found</div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left py-2 px-2">Time</th>
                              <th className="text-left py-2 px-2">Event</th>
                              <th className="text-left py-2 px-2">User</th>
                              <th className="text-left py-2 px-2">Details</th>
                              <th className="text-left py-2 px-2">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {activityLogs.map((log, i) => (
                              <tr key={log.id || i} className="border-b hover:bg-slate-50">
                                <td className="py-2 px-2 whitespace-nowrap text-muted-foreground">
                                  {formatLogDate(log.timestamp)}
                                </td>
                                <td className="py-2 px-2">
                                  <Badge className={getEventTypeColor(log.event_type)}>
                                    {log.event_type.replace(/_/g, ' ')}
                                  </Badge>
                                </td>
                                <td className="py-2 px-2">
                                  <span className="font-medium">{log.user_email || '-'}</span>
                                  {log.target_user_email && (
                                    <span className="text-muted-foreground"> → {log.target_user_email}</span>
                                  )}
                                </td>
                                <td className="py-2 px-2 max-w-xs truncate" title={log.details}>
                                  {log.details || '-'}
                                </td>
                                <td className="py-2 px-2">
                                  {log.success ? (
                                    <CheckCircle className="w-4 h-4 text-green-500" />
                                  ) : (
                                    <XCircle className="w-4 h-4 text-red-500" />
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
);
